import { ApplicationElement, html } from '@beunity/lit-components';
import style from './style.lit.css';

/**
 * Simple wrapper for block elements.
 * * `theme` => define component background color<br>
 * * `gap` => auto adding bottom margin to all first children<br>
 * Has a breakpoint variants (respects mobile first approach), e.g. `gapSm` wil be affected on media query above 768px.
 * * `block` => define component block padding<br>
 * Has a breakpoint variants (respects mobile first approach), e.g. `blockSm` wil be affected on media query above 768px.
 * * `inline` => define component inline padding<br>
 * Has a breakpoint variants (respects mobile first approach), e.g. `inlineSm` wil be affected on media query above 768px.
 *
 * @prop theme - (options: /, white)
 * @prop gap - The gap between main content items (options: /, 0, 1, 2, 3, 4)
 * @prop gapXs - The gap between main content items => above 576px (options: /, 0, 1, 2, 3, 4)
 * @prop gapSm - The gap between main content items => above 768px (options: /, 0, 1, 2, 3, 4)
 * @prop gapMd - The gap between main content items => above 992px (options: /, 0, 1, 2, 3, 4)
 * @prop gapLg - The gap between main content items => above 1200px (options: /, 0, 1, 2, 3, 4)
 * @prop block - Padding block (options: /, 0, 1, 2, 3, 4)
 * @prop blockXs - Padding block => above 576px (options: /, 0, 1, 2, 3, 4)
 * @prop blockSm - Padding block => above 768px (options: /, 0, 1, 2, 3, 4)
 * @prop blockMd - Padding block => above 992px (options: /, 0, 1, 2, 3, 4)
 * @prop blockLg - Padding block => above 1200px (options: /, 0, 1, 2, 3, 4)
 * @prop inline - Inline block (options: /, 0, 1, 2, 3, 4)
 * @prop inlineXs - Inline block => above 576px (options: /, 0, 1, 2, 3, 4)
 * @prop inlineSm - Inline block => above 768px (options: /, 0, 1, 2, 3, 4)
 * @prop inlineMd - Inline block => above 992px (options: /, 0, 1, 2, 3, 4)
 * @prop inlineLg - Inline block => above 1200px (options: /, 0, 1, 2, 3, 4)
 *
 * @slot default - Main slot for content
 *
 * @cssprop --section-gap - The gap between main content items, defaults to 32px.
 * @cssprop --section-background - Component background
 * @cssprop --section-padding-block - Component block padding
 * @cssprop --section-padding-inline - Component inline padding
 */

export default class Section extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    theme: { type: String, reflect: true },
    themeMobile: { type: String, reflect: true },
    themeDesktop: { type: String, reflect: true },
    gap: { type: Number, reflect: true },
    gapXs: { type: Number, reflect: true },
    gapSm: { type: Number, reflect: true },
    gapMd: { type: Number, reflect: true },
    gapLg: { type: Number, reflect: true },
    block: { type: Number, reflect: true },
    blockXs: { type: Number, reflect: true },
    blockSm: { type: Number, reflect: true },
    blockMd: { type: Number, reflect: true },
    blockLg: { type: Number, reflect: true },
    inline: { type: Number, reflect: true },
    inlineXs: { type: Number, reflect: true },
    inlineSm: { type: Number, reflect: true },
    inlineMd: { type: Number, reflect: true },
    inlineLg: { type: Number, reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
  }

  get isDesktop() {
    return window.innerWidth >= 768;
  }

  handleSlotChange(e) {
    const items = e.target.assignedElements({ flatten: true });
    if (items.length) return;

    this.innerHTML = ''; // hide component => :host(:empty) { display: none; }
  }

  render() {
    return html`<slot @slotchange=${this.handleSlotChange}></slot>`;
  }
}

customElements.get('bu-section') ||
  customElements.define('bu-section', Section);
